// The signed-in shell (legacy AppLayout): the LOGGED IN AS bar for staff, sidebar, toolbar (Quick Add, the theme menu on dev and staging, Admin Center, user menu), the connectivity banner
// and, on the location node, the "local Backoffice" note. Pages render into the outlet.
import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useRouter } from '@tanstack/react-router';
import { DropdownMenu } from 'radix-ui';
import { KeyRound, LayoutDashboard, LogOut, User } from 'lucide-react';
import { startRegistration } from '@simplewebauthn/browser';
import { AppFrame, useToast, type Appearance, type NavNode } from '@radish/ui-web';
import type { PermissionKey } from '@radish/contracts';
import { useActiveLocation } from '../app/location.tsx';
import { ConnectivityBanner } from '../env/availability.tsx';
import { useApi } from '../data/api.tsx';
import { useEntitlements } from '../data/entitlements.ts';
import { useEffectiveSettings } from '../data/settings.ts';
import { endSession, initialsOf, signIn } from '../session/session.ts';
import { pathFor, type AppPath } from '../router-paths.ts';
import { LocationSwitcher } from './LocationSwitcher.tsx';
import { SIDEBAR, visibleNav } from './nav.tsx';
import { useOpenLateSales } from '../features/online/data.ts';
import { useAppearance } from './appearance.tsx';
import { QuickAddMenu } from './quick-add.tsx';
import { ImpersonationBar } from './impersonation-bar.tsx';
import { APPEARANCES, HEADER_ACTION, ThemeMenu, showsThemeMenu } from './header-actions.tsx';
import { PasskeyStepUpDialog, STEP_UP_FAILED } from './passkey-step-up.tsx';


/**
 * Registers a passkey for the signed-in person (doc 11): the browser's ceremony, then the server stores the key. Adding
 * one needs a step-up within five minutes (G-X22): on `step_up_required` it asks for the password first (`onStepUp`),
 * then runs again on the renewed token.
 */
function useAddPasskey(onStepUp: () => void) {
  const { auth } = useApi();
  const location = useActiveLocation();
  const toast = useToast();
  return async (token: string = location.session.token) => {
    const options = await auth.passkeyRegistrationOptions(token);
    if (!options.ok) return options.error.code === 'step_up_required' ? onStepUp() : toast.error(options.error.message);
    let response: unknown;
    try {
      response = await startRegistration({ optionsJSON: options.data.options as never });
    } catch {
      return toast.warning('The passkey was not added.');
    }
    const result = await auth.registerPasskey(token, { response });
    if (result.ok) return toast.success('Passkey added. You can use it to sign in.');
    return toast.error(result.error.message);
  };
}

/** Legacy's q-item: 48 px rows with the icon in a 56 px avatar column (AppHeaderUserMenu.vue). */
const ITEM = 'flex h-12 cursor-pointer items-center px-4 text-menu-ink outline-none data-[highlighted]:bg-menu-item-hover';
const ITEM_ICON = 'mr-4 w-6 shrink-0';

function UserMenu() {
  const location = useActiveLocation();
  const { appearance, setAppearance } = useAppearance();
  const navigate = useNavigate();
  const router = useRouter();
  const { auth } = useApi();
  const [steppingUp, setSteppingUp] = useState(false);
  const addPasskey = useAddPasskey(() => setSteppingUp(true));
  // The step-up renews the session; the passkey is added on the new token, which carries the step-up time.
  const stepUp = async (password: string): Promise<string | null> => {
    const result = await auth.stepUp(location.session.token, { password });
    if (!result.ok) return result.error.code === 'too_many_attempts' ? result.error.message : STEP_UP_FAILED;
    signIn(result.data);
    setSteppingUp(false);
    if (result.data.accessToken !== null) void addPasskey(result.data.accessToken);
    return null;
  };
  const { user, accounts, current } = location.session.view;
  // Radish staff, resellers and people in more than one account pick the account they act in (legacy /accounts).
  const canSwitchAccount = accounts.length > 1 || current.via !== 'membership';
  return (
    <>
    <DropdownMenu.Root>
      <DropdownMenu.Trigger aria-label="Account menu" data-testid="user-menu" className={`${HEADER_ACTION} text-[16px] font-semibold tracking-[0.02em]`}>
        {initialsOf(user.name)}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={6} className="z-[3000] min-w-[142px] rounded-[4px] bg-menu text-[14px] text-menu-ink-body shadow-lg dark:shadow-none">
          {/* Legacy's menu: Profile, a rule, then Sign Out (Flags is legacy's dev-only split tool; Next has none). */}
          <DropdownMenu.Item className={ITEM} onSelect={() => void navigate({ to: '/l/$slug/profile', params: { slug: location.slug } })}>
            <User size={22} aria-hidden className={ITEM_ICON} />Profile
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="h-px bg-line" />
          {/* On a phone legacy puts Appearance here, above Sign Out (its header has no theme control there). */}
          <div className="min-[600px]:hidden" data-testid="user-menu-appearance">
            <DropdownMenu.Label className="px-4 py-2 text-[12px] text-text-3">Appearance</DropdownMenu.Label>
            <DropdownMenu.RadioGroup value={appearance} onValueChange={(v) => setAppearance(v as Appearance)}>
              {APPEARANCES.map((a) => (
                <DropdownMenu.RadioItem key={a.value} value={a.value} className="flex h-9 cursor-pointer items-center px-4 outline-none data-[highlighted]:bg-menu-item-hover data-[state=checked]:font-medium data-[state=checked]:text-primary">
                  {a.label}
                </DropdownMenu.RadioItem>
              ))}
            </DropdownMenu.RadioGroup>
            <DropdownMenu.Separator className="h-px bg-line" />
          </div>
          {/* Next's own: staff and multi-account users switch accounts; anyone may add a passkey (doc 11). */}
          {canSwitchAccount ? (
            <DropdownMenu.Item className={ITEM} onSelect={() => void navigate({ to: '/accounts' })}><span className={ITEM_ICON} />Switch Account</DropdownMenu.Item>
          ) : null}
          <DropdownMenu.Item className={ITEM} onSelect={() => void addPasskey()}><KeyRound size={22} aria-hidden className={ITEM_ICON} />Add a Passkey</DropdownMenu.Item>
          <DropdownMenu.Item
            className={ITEM}
            onSelect={() => {
              // Legacy's Sign Out pushes '/login?' + Date.now(), a bare ?<ms> with no key (AppHeaderUserMenu.vue; Q-NAV-16).
              // Pushed on the history as is: the router's own navigate would write it ?<ms>=.
              void endSession().then(() => router.history.push(`/login?${String(Date.now())}`));
            }}
          >
            <LogOut size={22} aria-hidden className={ITEM_ICON} />Sign Out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
    {steppingUp ? <PasskeyStepUpDialog onConfirm={stepUp} onClose={() => setSteppingUp(false)} /> : null}
    </>
  );
}

interface AppShellProps {
  readonly title: string;
  readonly path: AppPath | null;
  readonly children: ReactNode;
  /** Lane D2: the Admin Center swaps the sidebar for its own menu (legacy only lists Gift Cards there). */
  readonly nav?: readonly NavNode[];
  readonly renderNavLink?: (node: NavNode, className: string, content: ReactNode) => ReactNode;
}

export function AppShell({ title, path, children, nav: navOverride, renderNavLink }: AppShellProps) {
  const location = useActiveLocation();
  const { environment } = useApi();
  const settings = useEffectiveSettings(location.locationId).settings;
  const billing = useEntitlements(location.locationId);
  const gate = { entitled: (feature: string) => billing.standing(feature) === 'on', onPlan: billing.onPlan };
  // Radish Direct gate 18 (A189): the open late card-charge alerts as a red count on Online Store, from any page.
  const lateSales = useOpenLateSales(location.locationId, location.can);
  const nav = navOverride === undefined
    ? visibleNav(SIDEBAR, (p: PermissionKey) => location.can(p), path, true, (key) => settings?.get(key) === true, gate)
      .map((node) => (node.id === 'online-store' && lateSales !== undefined ? { ...node, count: lateSales } : node))
    : [...navOverride];
  // The router forces aria-current on any link it matches by prefix, and Dashboard's `/l/$slug` prefixes every page, so an
  // entry the sidebar doesn't light matches exactly (B2025; page.tsx's tabs do the same).
  const renderLink = (node: NavNode, className: string, content: ReactNode) => (
    <Link to={pathFor((node.href ?? '/l/$slug') as AppPath, location.slug)} activeOptions={{ exact: !node.active }} className={className} aria-current={node.active ? 'page' : undefined}>
      {content}
    </Link>
  );
  return (
    <AppFrame
      logoSrc="/radish-logo-dark.svg"
      nav={nav}
      renderLink={renderNavLink ?? renderLink}
      title={title}
      routeKey={path ?? ''}
      locationSwitcher={<LocationSwitcher />}
      topBar={
        <>
          <ImpersonationBar />
          {environment.profile === 'location' ? (
            <div className="bg-sidebar px-4 py-1 text-center text-[12px] text-white" data-testid="local-backoffice-bar">
              Local Backoffice on the store's node: cloud-only features need internet.
            </div>
          ) : null}
        </>
      }
      toolbarEnd={
        <>
          <QuickAddMenu />
          {showsThemeMenu(window.location.hostname, import.meta.env.DEV) ? <ThemeMenu /> : null}
          {location.can('giftCards.settle') ? (
            // Legacy: the dashboard-icon button to the Admin Center, for users with admin-center access.
            <Link to="/admin-center/$accountId/gift-cards" params={{ accountId: location.session.view.current.accountId }} aria-label="Admin Center" data-testid="admin-center-button"
              className="inline-flex size-9 items-center justify-center rounded-q text-primary">
              <LayoutDashboard size={20} aria-hidden />
            </Link>
          ) : null}
          <UserMenu />
        </>
      }
    >
      <ConnectivityBanner />
      {children}
    </AppFrame>
  );
}
